/* eslint-disable react/forbid-prop-types */

import { datadogRum } from '@datadog/browser-rum';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Container } from 'semantic-grab-ui/src';

import { getUserPermissions } from 'actions/user';
import SideBar from 'components/sidebar/SideBar';
import { getSidebarLinks } from 'links';
import { BASE_NAMESPACE } from 'namespace';
import { getNamespacedUrl } from 'utils/route-utils';
import './AppContainer.scss';
import TopNav from './TopNav';

export class AppContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isFullView: false,
      isSidebarVisible: true,
    };
  }

  /**
   * Handles redirection of external and links
   *
   * @param {string} url New url to redirect to
   */
  redirect(url) {
    const { origin } = window.location;

    if (url.startsWith(origin)) {
      this.props.router.push(url.replace(origin, ''));
    } else if (url.startsWith('http')) {
      window.location.href = url;
    } else {
      this.props.router.push(url);
    }
  }

  /**
   * Redirects to base url with redirect query
   */
  redirectLoggedOut() {
    const { pathname, search } = window.location;
    const { BASE_URL } = window.Grab.config;
    let redirectQuery = '/login';

    // Add redirect parameter
    if (pathname !== '/' && pathname !== redirectQuery) {
      redirectQuery += `?redirect=${encodeURIComponent(
        `${pathname}${search}`,
      )}`;
    }

    // Add full url if not in base namespace
    if (this.props.namespace !== BASE_NAMESPACE) {
      redirectQuery = `https://${BASE_URL}${redirectQuery}`;
    }

    this.redirect(redirectQuery);
  }

  /**
   * Redirect a logged in user to the correct namespaced omega app
   *
   * @param {string | undefined} path The path to redirect to
   * @param {{ to: String, name: String, namespace: String }[]} sidebarLinks Array of links that the user is authorized to view
   */
  redirectLoggedIn(path, sidebarLinks) {
    const { namespace } = this.props;

    // If not authorized to view any links
    if (sidebarLinks.length === 0) {
      return this.redirect('/home');
    }

    let defaultUrl = sidebarLinks[0].to;

    // Construct external link if namespace
    if (namespace !== sidebarLinks[0].namespace) {
      defaultUrl = getNamespacedUrl(sidebarLinks[0].namespace, defaultUrl);
    }

    if (!path || path === '/') {
      return this.redirect(defaultUrl);
    }

    // Find link that redirectURL belongs to
    const link = sidebarLinks.find(({ to }) => RegExp(to).test(path));
    if (!link) {
      return this.redirect(defaultUrl);
    }

    // Construct external link for different namespace
    if (namespace !== link.namespace) {
      return this.redirect(getNamespacedUrl(link.namespace, path));
    }

    this.redirect(path);
  }

  componentDidMount() {
    this.initRum();
    if (!this.props.isLoggedIn && this.props.location.pathname !== '/login') {
      this.redirectLoggedOut();
    } else if (this.props.isLoggedIn) {
      const { email } = this.props;
      this.props.getUserPermissions();
      if (email) {
        datadogRum.setUser({
          id: email,
          email,
        });
      }
    }
  }

  // Update the module name based on the path
  // This is used to track the module name in Datadog
  updateModuleName(path) {
    if (!path) {
      return;
    }

    let module = 'tc';
    if (path.startsWith('/campaigns')) {
      module = 'trident';
    } else if (path.startsWith('/ucd')) {
      module = 'holos';
    }

    datadogRum.setGlobalContextProperty('module', module);
  }

  initRum() {
    datadogRum.init({
      applicationId: window.Grab.config.RUM_APPID,
      clientToken: window.Grab.config.RUM_CLIENT_TOKEN,
      service: 'tc',
      site: 'datadoghq.com',
      env: window.Grab.config.ENV || 'dev',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'allow',
    });
    this.updateModuleName(window.location.pathname);
  }

  componentDidUpdate(prevProps) {
    const {
      isLoggedIn,
      location,
      userPermissions,
      getUserPermissions,
      namespace,
      email,
    } = this.props;

    // Update service name when route changes
    if (prevProps.location.pathname !== location.pathname) {
      this.updateModuleName(location.pathname);
    }

    // Redirect to login when logged out
    if (prevProps.isLoggedIn && !isLoggedIn && location.pathname !== '/login') {
      this.redirectLoggedOut();

      // Fetch user permissions once logged in
    } else if (!prevProps.isLoggedIn && isLoggedIn) {
      getUserPermissions();

      // Redirect to page once logged in
    } else if (
      isLoggedIn &&
      ['', '/', '/login'].includes(location.pathname) &&
      userPermissions
    ) {
      // location.query.state contains data that is passed from concedo login, we use it as a redirect url
      this.redirectLoggedIn(
        location.query.state,
        getSidebarLinks(userPermissions, namespace, email),
      );
    }
  }

  triggerFullView = value => {
    this.setState({
      isFullView: value,
    });
  };

  toggleSidebar = () => {
    this.setState(prevState => ({
      isSidebarVisible: !prevState.isSidebarVisible,
    }));
  };

  render() {
    const {
      isLoggedIn,
      hasDetailsHeader,
      namespace,
      userPermissions,
      email,
    } = this.props;

    const { isFullView, isSidebarVisible } = this.state;

    const sidebarLinks = userPermissions
      ? getSidebarLinks(userPermissions, namespace, email)
      : [];

    const children = isLoggedIn ? (
      this.props.children
    ) : (
      <Container textAlign="center">{this.props.children}</Container>
    );

    const wrapClassName = `-wrap${isFullView ? '-fullview' : ''}`;

    return (
      <div>
        <input type="checkbox" id="toggle-sidebar" />
        <section className={wrapClassName}>
          <section
            className={classnames('-body', {
              '-notLoggedIn': !isLoggedIn,
              '-sidebar-hidden': !isSidebarVisible && isLoggedIn && !isFullView,
            })}
            style={{
              marginLeft: (!isSidebarVisible && isLoggedIn && !isFullView) ? '0px' : undefined,
              transition: 'margin-left 0.3s ease-in-out',
            }}
          >
            {!isFullView && <TopNav />}
            <main
              className={classnames('-content', {
                '-notLoggedIn': !isLoggedIn,
                'details-header': hasDetailsHeader,
              })}
              style={{
                width: (!isSidebarVisible && isLoggedIn && !isFullView) ? '100vw' : undefined,
                transition: 'width 0.3s ease-in-out',
              }}
            >
              {React.Children.map(children, child =>
                React.cloneElement(
                  child,
                  isLoggedIn && {
                    sidebarLinks,
                    ...{ triggerFullView: this.triggerFullView },
                  },
                ),
              )}
            </main>
          </section>
          {isLoggedIn && !isFullView && isSidebarVisible && (
            <SideBar
              sidebarLinks={sidebarLinks}
              pathname={this.props.location.pathname}
              namespace={namespace}
            />
          )}
          {isLoggedIn && !isFullView && (
            <button
              onClick={this.toggleSidebar}
              style={{
                position: 'fixed',
                left: isSidebarVisible ? '240px' : '-16px',
                top: '70px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '1px solid #ddd',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#666',
                zIndex: 1003,
                transition: 'left 0.3s ease-in-out, transform 0.2s ease',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
                e.target.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.backgroundColor = '#fff';
              }}
              title={isSidebarVisible ? 'Hide Sidebar' : 'Show Sidebar'}
            >
              {isSidebarVisible ? '<' : '>'}
            </button>
          )}
        </section>
      </div>
    );
  }
}

AppContainer.propTypes = {
  namespace: PropTypes.string,
  isLoggedIn: PropTypes.bool.isRequired,
  hasDetailsHeader: PropTypes.bool.isRequired,
  userPermissions: PropTypes.object,
  email: PropTypes.string,
  router: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  getUserPermissions: PropTypes.func.isRequired,
  user: PropTypes.object,
};

const mapStateToProps = ({
  app: {
    user: { userPermissions, email },
    isLoggedIn,
    hasDetailsHeader,
  },
}) => ({
  userPermissions,
  isLoggedIn,
  hasDetailsHeader: Boolean(hasDetailsHeader), // If hasDetailsHeader is undefined then, Boolean() will convert it to false.
  email,
});

const mapDispatchToProps = {
  getUserPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
